import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import styles from './index.module.css';
import { NotificationScrollArea } from './NotificationScrollArea';
import { DeleteIcon } from '../../../assets';


const FILTERS = {
  ALL: 'all',
  UNREAD: 'unread',
};

const GROUP_ORDER = ['TODAY', 'YESTERDAY', 'EARLIER'];

export const AlertsPanel = ({ alerts = [], onViewAll, onDelete, onOpen }) => {
  const [filter, setFilter] = useState(FILTERS.ALL);
  const panelRef = useRef(null);
  const listRef = useRef(null);

  const groupedAlerts = useMemo(() => {
    const filtered =
      filter === FILTERS.UNREAD ? alerts.filter((alert) => alert.unread) : alerts;

    return GROUP_ORDER.map((group) => ({
      group,
      items: filtered.filter((alert) => alert.group === group),
    })).filter(({ items }) => items.length > 0);
  }, [alerts, filter]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return undefined;

    const handleWheel = (event) => {
      event.preventDefault();
      event.stopPropagation();

      const list = listRef.current;
      if (!list) return;

      list.scrollTop += event.deltaY;
    };

    panel.addEventListener('wheel', handleWheel, { passive: false });
    return () => panel.removeEventListener('wheel', handleWheel);
  }, []);

  return (
    <div className={styles.panel} ref={panelRef}>
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>Alerts & Updates</h2>
        <div className={styles.tabs} role="tablist" aria-label="Alert filters">
          <button
            type="button"
            role="tab"
            aria-selected={filter === FILTERS.ALL}
            className={classnames(styles.tab, {
              [styles.tabActive]: filter === FILTERS.ALL,
            })}
            onClick={() => setFilter(FILTERS.ALL)}
          >
            All
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={filter === FILTERS.UNREAD}
            className={classnames(styles.tab, {
              [styles.tabActive]: filter === FILTERS.UNREAD,
            })}
            onClick={() => setFilter(FILTERS.UNREAD)}
          >
            Unread
          </button>
        </div>
      </div>

      <NotificationScrollArea className={styles.list} contentRef={listRef}>
        {groupedAlerts.length === 0 ? (
          <div className={styles.emptyState}>No alerts to show</div>
        ) : (
          groupedAlerts.map(({ group, items }) => (
            <section key={group} className={styles.group}>
              <div className={styles.groupHeader}>{group}</div>
              <ul className={styles.groupList}>
                {items.map((alert) => (
                  <li key={alert.id} className={styles.alertItem}>
                    <div
                      className={styles.alertBody}
                      onClick={() => onOpen?.(alert)}
                    >
                      <p className={styles.alertTitle}>{alert.title}</p>
                      {alert.description && (
                        <p className={styles.alertDescription}>{alert.description}</p>
                      )}
                      {alert.meta && <p className={styles.alertMeta}>{alert.meta}</p>}
                      <p className={styles.alertTimestamp}>{alert.timestamp}</p>
                    </div>
                    <div className={styles.alertActions}>
                      {alert.unread && (
                        <span className={styles.unreadDot} aria-label="Unread" />
                      )}
                      <button
                        type="button"
                        className={styles.deleteBtn}
                        aria-label={`Delete ${alert.title}`}
                        onClick={() => onDelete?.(alert.id)}
                      >
                        <DeleteIcon width={16} height={16} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </NotificationScrollArea>

      <div className={styles.panelFooter}>
        <button type="button" className={styles.viewAllBtn} onClick={onViewAll}>
          View All
        </button>
      </div>
    </div>
  );
};

AlertsPanel.propTypes = {
  alerts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      group: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      meta: PropTypes.string,
      timestamp: PropTypes.string.isRequired,
      unread: PropTypes.bool,
    })
  ),
  onViewAll: PropTypes.func,
  onDelete: PropTypes.func,
  onOpen: PropTypes.func,
};
